import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense, useEffect, useRef } from "react";
import "./utils/axiosSetup"; // Global axios interceptor - auto-attaches JWT token
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ConfigProvider } from "./contexts/ConfigContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { RideProvider, useRide } from "./contexts/RideContext";
import PageBackground from "./components/PageBackground";
import ErrorBoundary from "./components/ErrorBoundary";
import PageErrorBoundary from "./components/PageErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import LoginPromptScreen from "./components/LoginPromptScreen";

// Wrap any page in a per-page error boundary so one crash doesn't take
// down the whole app. `resetKey` is tied to the current pathname so a
// crash on one route auto-clears when the user navigates away.
const Safe = ({ children, name }) => {
  const location = useLocation();
  return (
    <PageErrorBoundary name={name} resetKey={location.pathname}>
      {children}
    </PageErrorBoundary>
  );
};
import RideFloatingIndicator from "./components/RideFloatingIndicator";
import AdminPageNumberBadge from "./components/AdminPageNumberBadge";

// Resilient dynamic import for code-split routes.
//
// Two distinct failure modes after a deploy:
//  1. Transient network blip (flaky 5G) → a couple of quick retries recover it.
//  2. DETERMINISTIC failure: the client still holds the PREVIOUS build's
//     main.js and requests an old chunk hash that no longer exists. The edge
//     serves index.html (200 text/html) for that missing /static/* path, so
//     the webpack loader executes HTML as JS → ChunkLoadError. Retrying import()
//     is useless (the bad 200-HTML response is deterministic). The ONLY fix is a
//     full page reload, which re-fetches the no-cache index.html → the CURRENT
//     main.js → the current chunk hashes. Guarded by sessionStorage so a
//     genuinely broken deploy can't cause an infinite reload loop.
const CHUNK_RELOAD_KEY = 'baremc.chunkReload';
function lazyWithRetry(factory, retries = 2, delayMs = 500) {
  return lazy(() =>
    new Promise((resolve, reject) => {
      const attempt = (n) => {
        factory()
          .then((mod) => {
            // Success → clear the guard so a LATER deploy in this same session
            // can also self-heal once.
            try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch (_e) { /* noop */ }
            resolve(mod);
          })
          .catch((loadErr) => {
            if (n > 0) { setTimeout(() => attempt(n - 1), delayMs); return; }
            // Retries exhausted → one-time full reload to pick up the current build.
            try {
              if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
                sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
                window.location.reload();
                return; // leave the promise pending; the page is reloading
              }
            } catch (_e) { /* sessionStorage unavailable → fall through */ }
            reject(loadErr); // already reloaded once → let ErrorBoundary show the UI
          });
      };
      attempt(retries);
    })
  );
}

// Critical pages - loaded immediately
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
// Tim's brief June 1, 2026 v27 — LAZY MAP:
//   MapPage importerer ReplayMap (3100+ linjer + MapLibre GL + canvas
//   helpers) → trækker hele replay-modulet ind i initial bundle, selv
//   når brugeren er på forsiden. Det giver 600-800 KB ekstra JS at
//   parse på iPhone Safari = laggy scroll + lang transition.
//   Fix: lazy-load MapPage så replay-koden først hentes når brugeren
//   faktisk navigerer til /map.
const MapPage = lazyWithRetry(() => import("./pages/MapPage"));

// All other pages - lazy loaded on demand
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const ClubsPage = lazyWithRetry(() => import("./pages/ClubsPage"));
const ClubDetailPage = lazyWithRetry(() => import("./pages/ClubDetailPage"));
const EventsPage = lazyWithRetry(() => import("./pages/EventsPage"));
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const AccountPage = lazyWithRetry(() => import("./pages/AccountPage"));
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"));
const MessagesPage = lazyWithRetry(() => import("./pages/MessagesPage"));
const RulesPage = lazyWithRetry(() => import("./pages/RulesPage"));
const ContactPage = lazyWithRetry(() => import("./pages/ContactPage"));
const OffersPage = lazyWithRetry(() => import("./pages/OffersPage"));
const SuperadminPage = lazyWithRetry(() => import("./pages/SuperadminPage"));
const FarliPage = lazyWithRetry(() => import("./pages/FarliPage"));
const PlanTripPage = lazyWithRetry(() => import("./pages/PlanTripPage"));
const ReplayLabPage = lazyWithRetry(() => import("./pages/ReplayLabPage"));
// QA preview - synkron import for at undgå Suspense-hæng
import TimelinePreviewPage from "./pages/TimelinePreviewPage";
import PageTurnTestPage from "./pages/PageTurnTestPage";
import TimelineShowcasePage from "./pages/TimelineShowcasePage";
import LivingTimelineConceptPage from "./pages/LivingTimelineConceptPage";
const RecordReplayPage = lazyWithRetry(() => import("./pages/RecordReplayPage"));
const UnsubscribePage = lazyWithRetry(() => import("./pages/UnsubscribePage"));
const UserProfilePage = lazyWithRetry(() => import("./pages/UserProfilePage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const HelpPage = lazyWithRetry(() => import("./pages/HelpPage"));
const AdminContentPage = lazyWithRetry(() => import("./pages/AdminContentPage"));
const DiscoverPage = lazyWithRetry(() => import("./pages/DiscoverPage"));
const FeedPage = lazyWithRetry(() => import("./pages/FeedPage"));
const WatchRidesPage = lazyWithRetry(() => import("./pages/WatchRidesPage"));
const ReelsPage = lazyWithRetry(() => import("./pages/ReelsPage"));
const PublicReelPage = lazyWithRetry(() => import("./pages/PublicReelPage"));
const RideResolverPage = lazyWithRetry(() => import("./pages/RideResolverPage"));
const PitstopApplyPage = lazyWithRetry(() => import("./pages/PitstopApplyPage"));
const CreateProfilePage = lazyWithRetry(() => import("./pages/CreateProfilePage"));
const MyRidesPage = lazyWithRetry(() => import("./pages/MyRidesPage"));
const SavedPostsPage = lazyWithRetry(() => import("./pages/SavedPostsPage"));
const LeaderboardPage = lazyWithRetry(() => import("./pages/LeaderboardPage"));
const PublicClubPage = lazyWithRetry(() => import("./pages/PublicClubPage"));
const PublicEventPage = lazyWithRetry(() => import("./pages/PublicEventPage"));
const ClubsDirectoryPage = lazyWithRetry(() => import("./pages/ClubsDirectoryPage"));
const CreateMeetupPage = lazyWithRetry(() => import("./pages/CreateMeetupPage"));
const CreateEventPage = lazyWithRetry(() => import("./pages/CreateEventPage"));
const AdminInboxPage = lazyWithRetry(() => import("./pages/AdminInboxPage"));
const MyMessagesPage = lazyWithRetry(() => import("./pages/MyMessagesPage"));
const CreatePitstopPage = lazyWithRetry(() => import("./pages/CreatePitstopPage"));
const MeetupDetailPage = lazyWithRetry(() => import("./pages/MeetupDetailPage"));
const PitstopDetailPage = lazyWithRetry(() => import("./pages/PitstopDetailPage"));
const AccommodationDetailPage = lazyWithRetry(() => import("./pages/AccommodationDetailPage"));
const AccommodationApplyPage = lazyWithRetry(() => import("./pages/AccommodationApplyPage"));
const EventDetailPage = lazyWithRetry(() => import("./pages/EventDetailPage"));
const PlateLookupPage = lazyWithRetry(() => import("./pages/PlateLookupPage"));
const FavoritesPage = lazyWithRetry(() => import("./pages/FavoritesPage"));
const AdminDebugRidesPage = lazyWithRetry(() => import("./pages/AdminDebugRidesPage"));
const TravelJournalListPage = lazyWithRetry(() => import("./pages/TravelJournalListPage"));
const TravelJournalEditorPage = lazyWithRetry(() => import("./pages/TravelJournalEditorPage"));
const TravelJournalViewerPage = lazyWithRetry(() => import("./pages/TravelJournalViewerPage"));

// Components
import FarliFloatingButton from "./components/FarliFloatingButton";
import InstallAppBanner from "./components/InstallAppBanner";
import MobileBottomNav from "./components/MobileBottomNav";
import { useRiderRadar } from "./hooks/useRiderRadar";

// Prefetch popular routes after initial load
const prefetchRoutes = () => {
  setTimeout(() => {
    import("./pages/ClubsPage");
    import("./pages/ClubDetailPage");
    import("./pages/DashboardPage");
    import("./pages/FarliPage");
  }, 3000);
};

// Background images per page group
const BG_IMAGES = {
  auth: 'https://images.unsplash.com/photo-1758115273591-af5cdc65d1ef?w=1920&q=80&auto=format',
  clubs: 'https://images.unsplash.com/photo-1579509554181-545d7cee73fb?w=1920&q=80&auto=format',
  events: 'https://images.unsplash.com/photo-1558981285-6f0c94958bb6?w=1920&q=80&auto=format',
  info: 'https://images.unsplash.com/photo-1762806354268-01a7defdb290?w=1920&q=80&auto=format',
  dashboard: 'https://images.unsplash.com/photo-1421344682218-763a7bcdf34b?w=1920&q=80&auto=format',
  farli: 'https://images.unsplash.com/photo-1764006942825-2439134edc2b?w=1920&q=80&auto=format',
  messages: 'https://images.unsplash.com/photo-1650667053713-f6c6328821a3?w=1920&q=80&auto=format',
  settings: 'https://images.unsplash.com/photo-1708579449068-1e886fed6550?w=1920&q=80&auto=format',
};

const getBackgroundForRoute = (pathname) => {
  if (['/login', '/register', '/glemt-adgangskode', '/reset-password'].some(p => pathname.startsWith(p))) return BG_IMAGES.auth;
  if (pathname.startsWith('/klubber')) return BG_IMAGES.clubs;
  if (pathname.startsWith('/events')) return BG_IMAGES.events;
  if (pathname.startsWith('/kontakt') || pathname.startsWith('/regler') || pathname.startsWith('/hjaelp')) return BG_IMAGES.info;
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/konto') || pathname.startsWith('/profil') || pathname.startsWith('/favoritter')) return BG_IMAGES.dashboard;
  if (pathname.startsWith('/farli') || pathname.startsWith('/plan-tur')) return BG_IMAGES.farli;
  if (pathname.startsWith('/beskeder') || pathname.startsWith('/mine-beskeder')) return BG_IMAGES.messages;
  if (pathname.startsWith('/opret-meetup') || pathname.startsWith('/opret-pitstop') || pathname.startsWith('/opret-event') || pathname.startsWith('/meetup/') || pathname.startsWith('/pitstop/') || pathname.match(/^\/events\/[^/]+$/)) return BG_IMAGES.events;
  return BG_IMAGES.settings;
};

// Lazy preload background images — only after initial render
const _preloadedBgs = new Set();
const preloadBg = (src) => {
  if (!_preloadedBgs.has(src)) {
    _preloadedBgs.add(src);
    const img = new Image();
    img.src = src;
  }
};

// Layout wrapper with route-aware parallax background
const BackgroundLayout = () => {
  const location = useLocation();
  const bgImage = getBackgroundForRoute(location.pathname);
  preloadBg(bgImage);
  return (
    <PageBackground bgImage={bgImage}>
      <Outlet />
    </PageBackground>
  );
};

// Protected Route Component

const ProtectedRoute = ({ children, allowedTypes = [], feature }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-400"></div>
      </div>
    );
  }
  
  if (!user) {
    return <LoginPromptScreen feature={feature} />;
  }
  
  // Admin access: check user_type OR is_admin/is_site_admin flag
  const hasAdminAccess = user.user_type === 'admin' || user.is_admin === true || user.is_site_admin === true;
  
  if (hasAdminAccess) {
    return children;
  }
  
  if (allowedTypes.length > 0 && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Invisible component that shares location when Rider Radar is enabled
const RiderRadarTracker = () => {
  useRiderRadar();
  return null;
};

// Remembers the user's last active MAIN section and resumes there on next
// app open. Deep links (detail pages, shared /profil/:id, QR links) are left
// untouched — we only auto-resume when the app opens on the default forside.
const MAIN_SECTIONS = ['/feed', '/kort', '/mine-ture', '/klubber', '/events', '/dashboard', '/profil'];
const LAST_SECTION_KEY = 'baremc_last_section';
const matchMainSection = (pathname) => {
  if (pathname === '/') return '/';
  for (const s of MAIN_SECTIONS) {
    if (pathname === s || pathname.startsWith(s + '/')) return s;
  }
  return null;
};
const LastSectionTracker = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    try {
      const saved = localStorage.getItem(LAST_SECTION_KEY);
      // Only resume when the app opened on the default forside.
      if (saved && saved !== '/' && window.location.pathname === '/') {
        navigate(saved, { replace: true });
      }
    } catch (_e) { /* ignore */ }
  }, [navigate]);

  useEffect(() => {
    const sec = matchMainSection(location.pathname);
    if (sec) {
      try { localStorage.setItem(LAST_SECTION_KEY, sec); } catch (_e) { /* ignore */ }
    }
  }, [location.pathname]);

  return null;
};

// Prefetch on mount
if (typeof window !== 'undefined') {
  window.addEventListener('load', prefetchRoutes, { once: true });
}

function RideWrapper({ children }) {
  const { user } = useAuth();
  return <RideProvider user={user}>{children}</RideProvider>;
}

// Sets document dir + lang attribute for RTL languages (Arabic)
function DocumentDirectionSync() {
  const { language } = useLanguage();
  useEffect(() => {
    const isRTL = language === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);
  return null;
}

function App() {
  const LazyFallback = (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0c1611' }}>
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-400"></div>
    </div>
  );

  return (
    <ErrorBoundary>
    <HelmetProvider>
    <AuthProvider>
      <ConfigProvider>
        <LanguageProvider>
          <div className="App noise-bg min-h-screen">
            <BrowserRouter>
              <DocumentDirectionSync />
              <ScrollToTop />
              <LastSectionTracker />
              <AdminPageNumberBadge />
              <RideWrapper>
              <Suspense fallback={LazyFallback}>
              <Routes>
                {/* Pages with their own background handling */}
                <Route path="/" element={<Safe name="Landing"><LandingPage /></Safe>} />
                <Route path="/kort" element={<Safe name="Map"><MapPage /></Safe>} />
                <Route path="/farli" element={
                  <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                    <Safe name="Farli"><FarliPage /></Safe>
                  </ProtectedRoute>
                } />

                {/* Public SEO pages */}
                <Route path="/club/:slug" element={<PublicClubPage />} />
                <Route path="/event/:slug" element={<PublicEventPage />} />
                <Route path="/clubs" element={<ClubsDirectoryPage />} />
                <Route path="/clubs/:filter" element={<ClubsDirectoryPage />} />
                <Route path="/r/:postId" element={<PublicReelPage />} />
                <Route path="/ride/:rideId" element={<RideResolverPage />} />
                <Route path="/rejsedagbog/v/:shareToken" element={<TravelJournalViewerPage />} />

                {/* Replay Lab — isolated mobile-friendly replay test surface.
                    Public so the URL can be shared/opened on mobile without
                    auth. Not wrapped in BackgroundLayout/Safe so a render
                    crash doesn't bubble up to a hard-reload. */}
                <Route path="/replay-lab/:rideId" element={<ReplayLabPage />} />

                {/* INTERN: server-side WYSIWYG-rendering. Åbnes KUN af Playwright. */}
                <Route path="/record-replay/:rideId" element={<RecordReplayPage />} />
                {/* QA: isoleret timeline preview — fjernes efter godkendelse */}
                <Route path="/timeline-preview" element={<TimelinePreviewPage />} />
                <Route path="/page-turn-test" element={<PageTurnTestPage />} />
                <Route path="/timeline-showcase" element={<Safe name="TimelineShowcase"><TimelineShowcasePage /></Safe>} />
                {/* QA: Living Timeline mockup — til godkendelse */}
                <Route path="/living-preview" element={<LivingTimelineConceptPage />} />

                {/* All other pages wrapped in global parallax background */}
                <Route element={<BackgroundLayout />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/glemt-adgangskode" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/klubber" element={<ClubsPage />} />
                  <Route path="/klubber/:id" element={<ClubDetailPage />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/regler" element={<RulesPage />} />
                  <Route path="/indstillinger" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <SettingsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/hjaelp" element={<HelpPage />} />
                  <Route path="/admin/indhold" element={<AdminContentPage />} />
                  <Route path="/kontakt" element={<Safe name="Kontakt"><ContactPage /></Safe>} />
                  <Route path="/tilbud" element={<OffersPage />} />
                  <Route path="/opret-profil" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <CreateProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="/pitstop-ansoeg" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <PitstopApplyPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/mine-ture" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <MyRidesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/rejsedagbog" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <TravelJournalListPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/rejsedagbog/ny" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <TravelJournalListPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/rejsedagbog/:id/rediger" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <TravelJournalEditorPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/debug-rides" element={
                    <ProtectedRoute allowedTypes={["admin", "superadmin"]}>
                      <AdminDebugRidesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/bibliotek" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <SavedPostsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/reels" element={
                    <Safe name="Reels"><ReelsPage /></Safe>
                  } />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  <Route path="/afmeld" element={<UnsubscribePage />} />
                  <Route path="/discover" element={<DiscoverPage />} />
                  <Route path="/nummerplade" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <PlateLookupPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/favoritter" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <FavoritesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/meetup/:id" element={<MeetupDetailPage />} />
                  <Route path="/pitstop/:id" element={<Safe name="PitstopDetail"><PitstopDetailPage /></Safe>} />
                  <Route path="/accommodation/:id" element={<AccommodationDetailPage />} />
                  <Route path="/opret-overnatning" element={<AccommodationApplyPage />} />
                  <Route path="/overnatning-ansoeg" element={<AccommodationApplyPage />} />
                  <Route path="/events/:id" element={<EventDetailPage />} />
                  <Route path="/admin-indbakke" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <Safe name="AdminInbox"><AdminInboxPage /></Safe>
                    </ProtectedRoute>
                  } />
                  <Route path="/mine-beskeder" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <Safe name="MineBeskeder"><MyMessagesPage /></Safe>
                    </ProtectedRoute>
                  } />
                  <Route path="/mine-beskeder/:threadId" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <Safe name="MineBeskeder"><MyMessagesPage /></Safe>
                    </ProtectedRoute>
                  } />
                  <Route path="/opret-meetup" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <CreateMeetupPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/opret-event" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <CreateEventPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/opret-pitstop" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <CreatePitstopPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/feed" element={
                    <Safe name="Feed"><FeedPage /></Safe>
                  } />
                  {/* "Watch Rides" — immersive vertical-snap ride feed.
                      Public route så ikke-logged-in besøgende også kan
                      mærke vibe'en (engagement-aktioner kræver login). */}
                  <Route path="/watch" element={
                    <Safe name="WatchRides"><WatchRidesPage /></Safe>
                  } />

                  {/* Protected routes */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute allowedTypes={["club", "company", "club_owner", "admin", "user"]}>
                      <DashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/konto" element={
                    <ProtectedRoute allowedTypes={["club", "company", "club_owner", "admin", "user"]}>
                      <AccountPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/beskeder" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin"]}>
                      <Safe name="Beskeder"><MessagesPage /></Safe>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin" element={
                    <ProtectedRoute allowedTypes={["admin", "superadmin"]}>
                      <Safe name="Admin"><AdminPage /></Safe>
                    </ProtectedRoute>
                  } />
                  <Route path="/superadmin" element={
                    <ProtectedRoute allowedTypes={["superadmin"]}>
                      <SuperadminPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/plan-tur" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <PlanTripPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/profil/:userId" element={
                    <Safe name="Profil"><UserProfilePage /></Safe>
                  } />
                  <Route path="/profil" element={
                    <ProtectedRoute allowedTypes={["user", "company", "club", "club_owner", "admin", "superadmin"]}>
                      <Safe name="Profil"><UserProfilePage /></Safe>
                    </ProtectedRoute>
                  } />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
          
              {/* Farli Floating Button - Navigates to /farli */}
              <FarliFloatingButton />
              <InstallAppBanner />
              <RiderRadarTracker />
              <RideFloatingIndicator />
              <MobileBottomNav />
              </RideWrapper>
            </BrowserRouter>
            <Toaster position="top-right" theme="dark" />
          </div>
      </LanguageProvider>
      </ConfigProvider>
    </AuthProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
